import React, { useRef } from "react";
import { useAppStore } from "./AppStore";
import Ico from "./Ico";
import { currentUser } from "../mock";
import { MapPin, Home as HomeIcon, CheckCircle2, ShieldCheck, Clock, Camera, X } from "lucide-react";

const fmt = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }); }
  catch { return ""; }
};

export default function SafeArrivalCard() {
  const { postedJobs, checkins, markArrived, markFinished, attachPhoto, removePhoto, completeDemoJob } = useAppStore();
  const parent = (currentUser.parent || "your parent").split(" ")[0];
  const fileRef = useRef(null);

  const job = postedJobs.find((j) => j.status === "parent_approved");
  if (!job) return null;

  const c = checkins[job.id] || {};
  const arrived = !!c.arrived;
  const finished = !!c.finished;

  const finishJob = () => {
    if (!arrived || finished) return;
    markFinished(job.id);
    completeDemoJob(job.pay); // pay out + advance rank (may trigger rank-up confetti)
  };

  const handleFile = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const max = 820;
        let { width, height } = img;
        if (width > height && width > max) { height = (height * max) / width; width = max; }
        else if (height > max) { width = (width * max) / height; height = max; }
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        try { attachPhoto(job.id, canvas.toDataURL("image/jpeg", 0.6)); }
        catch { attachPhoto(job.id, ev.target.result); }
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="gd-card overflow-hidden p-4">
      <div className="flex items-center gap-2">
        <div className="grid h-9 w-9 place-items-center rounded-full bg-[#eaf6ea]"><ShieldCheck size={18} className="text-[#4fae3f]" /></div>
        <div className="flex-1">
          <h3 className="text-sm font-extrabold text-[#1f2a52]">Safe Arrival Check</h3>
          <p className="text-[11px] font-semibold text-[#9aa3c0]">Tap to quietly reassure {parent}</p>
        </div>
        <span className="flex items-center gap-1 rounded-full bg-[#eaf6ea] px-2 py-1 text-[9px] font-bold text-[#3d8f34]">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#4fae3f]" /> ACTIVE JOB
        </span>
      </div>

      {/* job */}
      <div className="mt-3 flex items-center gap-3 rounded-2xl bg-[#f7f8fd] p-2.5">
        <div className="grid h-10 w-10 place-items-center rounded-xl" style={{ background: `${job.tint}18` }}><Ico name={job.icon} size={18} color={job.tint} /></div>
        <div className="flex-1"><p className="text-[13px] font-bold text-[#1f2a52]">{job.title}</p><p className="text-[11px] font-semibold text-[#9aa3c0]">{job.schedule}</p></div>
      </div>

      {/* steps */}
      <div className="mt-3 space-y-2.5">
        {arrived ? (
          <div className="flex items-center gap-3 rounded-2xl bg-[#eaf6ea] px-3 py-3">
            <CheckCircle2 size={20} className="text-[#4fae3f]" />
            <div className="flex-1"><p className="text-[13px] font-extrabold text-[#2e7d27]">Arrived safely</p><p className="text-[10px] font-semibold text-[#5b7a55]">Logged at {fmt(c.arrived)} · {parent} notified</p></div>
          </div>
        ) : (
          <button onClick={() => markArrived(job.id)} className="gd-tap flex w-full items-center justify-center gap-2 rounded-2xl py-3.5 text-sm font-extrabold text-white"
            style={{ background: "linear-gradient(180deg,#57b24a,#3d8f34)", boxShadow: "0 10px 20px rgba(61,143,52,.3)" }}>
            <MapPin size={18} /> I've Arrived Safely
          </button>
        )}

        {finished ? (
          <div className="flex items-center gap-3 rounded-2xl bg-[#eef2ff] px-3 py-3">
            <CheckCircle2 size={20} className="text-[#3b57c9]" />
            <div className="flex-1"><p className="text-[13px] font-extrabold text-[#2b3f96]">Finished — heading home</p><p className="text-[10px] font-semibold text-[#5b648a]">Logged at {fmt(c.finished)} · <span className="font-extrabold text-[#3d8f34]">+${job.pay} earned</span> · {parent} notified</p></div>
          </div>
        ) : (
          <button onClick={finishJob} disabled={!arrived}
            className="gd-tap flex w-full items-center justify-center gap-2 rounded-2xl py-3.5 text-sm font-extrabold transition-all"
            style={{ background: arrived ? "linear-gradient(180deg,#3f5ccb,#2f47a8)" : "#eef1fb", color: arrived ? "#fff" : "#b6bdd6", boxShadow: arrived ? "0 10px 20px rgba(47,71,168,.3)" : "none" }}>
            <HomeIcon size={18} /> Job Finished — Get Paid ${job.pay}
          </button>
        )}

        {/* photo proof — optional, available once finished */}
        {finished && (
          <div>
            <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={handleFile} className="hidden" />
            {c.photo ? (
              <div className="relative overflow-hidden rounded-2xl">
                <img src={c.photo} alt="Job proof" className="h-40 w-full object-cover" />
                <div className="absolute left-2 top-2 flex items-center gap-1 rounded-full bg-black/55 px-2 py-1 text-[10px] font-bold text-white">
                  <CheckCircle2 size={12} className="text-[#7ee787]" /> Photo proof attached
                </div>
                <button onClick={() => removePhoto(job.id)} className="gd-tap absolute right-2 top-2 grid h-7 w-7 place-items-center rounded-full bg-black/55 text-white"><X size={15} /></button>
                <button onClick={() => fileRef.current && fileRef.current.click()} className="gd-tap absolute bottom-2 right-2 flex items-center gap-1 rounded-full bg-white/90 px-3 py-1.5 text-[11px] font-extrabold text-[#2b3f96]">
                  <Camera size={13} /> Retake
                </button>
              </div>
            ) : (
              <button onClick={() => fileRef.current && fileRef.current.click()}
                className="gd-tap flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-[#c7cde6] py-3 text-sm font-bold text-[#5b648a]">
                <Camera size={17} className="text-[#3b57c9]" /> Add Photo Proof <span className="text-[11px] font-semibold text-[#9aa3c0]">(optional)</span>
              </button>
            )}
          </div>
        )}
      </div>

      <p className="mt-3 flex items-center gap-1.5 text-[10px] font-semibold text-[#9aa3c0]">
        <Clock size={12} /> {parent} sees these check-ins {finished ? "and photo " : ""}live — no need to text.
      </p>
    </div>
  );
}
